import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi } from "@/lib/api-client";
import { Command, Sparkles, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const { user, patchUser } = useAuth();
  const needsOnboarding = user && user.onboarding_completed === false;
  const [name, setName] = useState(user?.command_center_name || "");
  const [busy, setBusy] = useState(false);

  if (!needsOnboarding) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Give your Command Center a name");
    setBusy(true);
    try {
      await authApi.updateSettings({ command_center_name: name.trim(), onboarding_completed: true });
      patchUser({ command_center_name: name.trim(), onboarding_completed: true });
      toast.success(`${name} is initialized. Net Worth Pulse online.`);
    } catch { toast.error("Setup failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true}>
      <DialogContent
        className="bg-[#0A0A0A] border border-amber-500/40 text-white max-w-lg p-0 rounded-none [&>button]:hidden"
        data-testid="onboarding-dialog"
      >
        <div className="p-8 relative overflow-hidden"
             style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.12), transparent 60%)" }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center border border-amber-500/40 bg-black">
              <Command size={18} className="text-amber-400"/>
            </div>
            <div className="uc-chip"><Sparkles size={11} className="text-amber-400"/> INITIALIZATION</div>
          </div>
          <h2 className="font-serif-display text-4xl leading-tight mb-3">
            Name your<br/><span className="italic text-amber-400">Command Center.</span>
          </h2>
          <p className="text-white/60 text-sm mb-6 leading-relaxed">
            This is the umbrella that ties every venture together. Choose a name that reflects your empire — it will appear across the OS.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <Input
              data-testid="onboarding-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="e.g. Hawthorne Holdings OS"
              className="h-12 rounded-none bg-black border-white/15 text-lg font-serif-display uc-focus"
            />
            <Button
              data-testid="onboarding-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full h-12 uc-btn-amber rounded-none uc-focus"
            >
              {busy ? <Loader2 className="animate-spin" size={16}/> : (
                <>Initialize Command Center <ChevronRight size={16} className="ml-2"/></>
              )}
            </Button>
            <div className="text-[11px] text-white/40 font-mono-uc text-center">
              Net Worth Pulse will initialize immediately after.
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
